import Vue from 'vue'
import axios from 'axios'
import moment from 'moment'

/**
 * The Store Pattern
 * actions -> mutations
 * Never mutate the store directly.
 */

// initial state
// state.stores
const state = {
  list: {},
  myStore: {},
  lastUpdated: null,
}

// getters
const getters = {
}

// actions - can be async
const actions = {
  /**
   * load all stores based on this address
   * @param {string} address - address to look up (expecting just a zip, which is fine)
   */
  LOAD_STORES_BY_ADDRESS ({ COMMIT }, { address, nextAvailableTimeslot = false, includeMedical = false }) {
    var vm = this
    vm.commit('errors/CLEAR_ERRORS')

    const data = {
      address,
      curbsideOnly: true,
      radius: 100,
      nextAvailableTimeslot,
    }

    // COVID: Remove once the healthcare program is done 
    if (includeMedical) {
      data.includeMedical = includeMedical;
    }

    // return a promise
    axios
      .all([
        axios.post('/commerce-api/v1/store/locator/address', data)
      ])
      .then(
        axios.spread((response) => {
          // console.log('~', response.data)
          const data = Object.assign({}, response.data, { address })
          vm.commit('stores/SET_STORES_LIST', data)
        })
      )
      .catch((error) => {
        error.type = 'changeStore'
        vm.dispatch('errors/HANDLE_ERROR', error)
      })
  },

  /**
   * update the server with a new pickup store
   * @param {string} pickupStoreId - store id
   * @param {boolean} [ignoreCartChangeWarnings] - whether to ignore the cart change warnings
   * @return {promise} promise returns the response status (number)
   */
  POST_STORE_CHANGE ({ COMMIT }, { pickupStoreId, ignoreCartChangeWarnings = false }) {
    var vm = this
    vm.commit('errors/CLEAR_ERRORS')

    const data = {
      ignoreCartChangeWarnings,
      pickupStoreId
    }

    return axios
      .all([
        axios.post('/commerce-api/v1/cart/fulfillment/pickup', data)
      ])
      .then(
        axios.spread((response) => {
          // console.log('~ POST_STORE_CHANGE()', response)
          const { status, data } = response

          // immediately update data used by the template
          vm.commit('stores/SET_MY_STORE', data)
          vm.commit('reservation/UPDATE_LOCATION', data.store)
          vm.commit('reservation/UPDATE_FULFILLMENT_TYPE', 'pickup')
          vm.commit('reservation/UPDATE_STORE', data.store)
          vm.commit('stores/CLEAR_STORES_LIST')

          // reload the whole cart
          vm.dispatch('cart/LOAD_CART')

          return status
        })
      )
      .catch((error) => {
        const { status } = error.response

        if (status === 409) {
          error.type = 'cartChange'
          error.variant = 'warning'
        } else {
          error.type = 'changeStore'
        }
        vm.dispatch('errors/HANDLE_ERROR', error)

        return status
      })
  }
}

// mutations - Must be synchronous
const mutations = {
  /**
   * cache a list of stores based on the address
   * @param {string} address - address to cache list for
   * @param {array} stores - list of stores
   */
  SET_STORES_LIST (state, { address, stores }) {
    const today = moment();
    const tomorrow = moment().add(1, 'days');

    // loop through stores to add relevant timeslot info
    stores.forEach(store => {
      let timeslotInfo = store.storeNextAvailableTimeslot;
      if (timeslotInfo) {
        const { nextAvailableTimeslotDate: date, serviceAvailable } = timeslotInfo;
        const isToday = moment(date).isSame(today, "day");
        const isTomorrow = moment(date).isSame(tomorrow, "day");

        if (isToday || isTomorrow) {
          timeslotInfo.status = "available-now";
          timeslotInfo.msg = `Next available pickup time is ${isToday ? "today" : "tomorrow"}`;
        } else if (date) {
          timeslotInfo.status = "available-soon";
          timeslotInfo.msg = `Next available pickup time is ${moment(date).format('dddd, MMM D')}`;
        } else if (serviceAvailable) {
          // if serviceAvailable is false, it means timeslots weren't fetched for this store
          // if it's true, that means the data was fetched but there are no timeslots available
          timeslotInfo.status = "not-available";
          timeslotInfo.msg = "All pickup times are full";
        }
      }
    });

    Vue.set(state, 'lastUpdated', Date.now());
    Vue.set(state.list, address, stores);
  },

  /**
   * clear the cached list of stores
   */
  CLEAR_STORES_LIST (state) {
    Vue.set(state, 'lastUpdated', null);
    Vue.set(state, 'list', {})
  },

  /**
   * set my store
   * @param {object} store - my store
   */
  SET_MY_STORE (state, { store }) {
    Vue.set(state, 'myStore', store)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
